import { Transforms, Editor, Range, Element, Path } from 'slate';
import { TableElement, TableRowElement, TableCellElement, TableFormat, BaseBlockFormat } from '../types';
import { createParagraph } from './paragraph';

export class TableUtil {
	constructor(private editor: Editor) {}

	insertTable = (rows: number, columns: number) => {
		const [tableNode] = Editor.nodes(this.editor, {
			match: (n: any) => !Editor.isEditor(n) && Element.isElement(n) && (n as any).type === 'table',
			mode: 'highest',
		});

		if (tableNode) return;
		if (!rows || !columns) {
			return;
		}
		// Creating a 2-d array of blank string as default text for the table
		const cellText = Array.from({ length: rows }, () => Array.from({ length: columns }, () => ''));
		const newTable = createTableNode(cellText, rows, columns);

		Transforms.insertNodes(this.editor, newTable as any, {
			mode: 'highest',
		});
		Transforms.insertNodes(this.editor, createParagraph('') as any, {
			mode: 'highest',
		});
	};

	removeTable = () => {
		Transforms.removeNodes(this.editor, {
			match: (n: any) => !Editor.isEditor(n) && Element.isElement(n) && (n as any).type === TableFormat.TABLE,
		});
	};

	insertRow = (action: 'after' | 'before') => {
		const { selection } = this.editor;

		if (!!selection && Range.isCollapsed(selection)) {
			const [tableNode] = Editor.nodes(this.editor, {
				match: (n: any) =>
					!Editor.isEditor(n) && Element.isElement(n) && (n as any).type === TableFormat.TABLE_ROW,
			});
			if (tableNode) {
				const [[table, tablePath]] = Editor.nodes(this.editor, {
					match: (n: any) =>
						!Editor.isEditor(n) && Element.isElement(n) && (n as any).type === TableFormat.TABLE,
				});
				const [, currentRow] = tableNode;

				const path = action === 'after' ? Path.next(currentRow) : currentRow;

				// @ts-ignore
				Transforms.insertNodes(this.editor, createRow(Array((table as any).columns).fill('')), {
					at: path,
				});
				Transforms.setNodes(this.editor, { rows: (table as any).rows + 1 } as any, {
					at: tablePath,
				});
			}
		}
	};

	insertColumn = (action: 'after' | 'before') => {
		const { selection } = this.editor;
		if (!!selection && Range.isCollapsed(selection)) {
			const [tableNode] = Editor.nodes(this.editor, {
				match: (n: any) =>
					!Editor.isEditor(n) && Element.isElement(n) && (n as any).type === TableFormat.TABLE_CELL,
			});
			if (tableNode) {
				const [[table, tablePath]] = Editor.nodes(this.editor, {
					match: (n: any) =>
						!Editor.isEditor(n) && Element.isElement(n) && (n as any).type === TableFormat.TABLE,
				});
				const [, currentCell] = tableNode;
				const startPath = action === 'after' ? Path.next(currentCell) : currentCell;

				// The last two indices of the path represents the row and column. We need to add one cell to each row starting from the first row
				startPath[startPath.length - 2] = 0;
				for (let row = 0; row < (table as any).rows; row += 1) {
					Transforms.insertNodes(this.editor, createTableCell(''), {
						at: startPath,
					});
					startPath[startPath.length - 2] += 1;
				}

				Transforms.setNodes(this.editor, { columns: (table as any).columns + 1 } as any, {
					at: tablePath,
				});
			}
		}
	};
}

const createRow = (cellText: string[]): TableRowElement => {
	const newRow = Array.from(cellText, (value) => createTableCell(value));
	return {
		type: TableFormat.TABLE_ROW,
		children: newRow,
	};
};

export const createTableCell = (text: string): TableCellElement => {
	return {
		type: TableFormat.TABLE_CELL,
		children: [createParagraph(text)] as any,
	};
};

const createTableNode = (cellText: string[][], rows: number, columns: number): TableElement => {
	const tableChildren = Array.from(cellText, (value) => createRow(value));
	const tableNode: TableElement = {
		type: TableFormat.TABLE,
		children: tableChildren,
		rows,
		columns,
	};
	return tableNode;
};
